import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { LANGUAGES, langToLabel } from '../i18n/languages'

const languages = LANGUAGES.map((code) => ({ code, label: langToLabel[code] }))

export default function LanguageSwitcher({ scrolled = false }: { scrolled?: boolean }) {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const current = languages.find((l) => l.code === i18n.language) ?? languages[0]

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const triggerColor = scrolled ? 'text-rot hover:text-rot/70' : 'text-sand hover:text-sand/70'

  return (
    <div ref={ref} className="relative flex items-center">
      <button
        onClick={() => setOpen(!open)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${current.label} language selector`}
        className={`min-h-[24px] min-w-[24px] p-1 text-xs tracking-widest font-semibold transition-colors flex items-center gap-1 font-body cursor-pointer ${triggerColor}`}
      >
        {current.label}
        <svg
          className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-lg py-1 min-w-[56px] z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                i18n.changeLanguage(lang.code)
                const nextUrl = new URL(window.location.href)
                nextUrl.searchParams.set('lang', lang.code)
                window.history.replaceState({}, '', nextUrl.toString())
                setOpen(false)
              }}
              className={`block w-full text-left px-4 py-1.5 text-xs tracking-widest transition-colors cursor-pointer ${
                i18n.language === lang.code
                  ? 'text-rot font-semibold bg-sand/50'
                  : 'text-espresso/70 hover:text-rot hover:bg-sand/30'
              }`}
            >
              {lang.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
